import { LitElement, html, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ExclusionListItem, GlobalExclusionsEvents } from '../../exclusionsPanel/types';
import { desanitizeObject } from '../../../utils/sanitization.utils';
import { htmlDecode } from '../../../utils/escape.utils';
import { Rights } from '@nirvana/shared/constants';

export class ExclusionListsWebComponent extends LitElement {
  @property({ type: String, reflect: true })
  companyExclusionListsTitle = '';

  @property({ type: Array, reflect: true })
  companyExclusionLists: ExclusionListItem[] = [];

  @property({ type: Array, reflect: true })
  userExclusionLists: ExclusionListItem[] = [];

  @property({ type: String, reflect: true })
  userExclusionListsTitle = '';

  @property({ type: String, reflect: true })
  newListString = '';

  @property({ type: String, reflect: true })
  allExclusionsString = '';

  @state()
  private activeList: ExclusionListItem | null;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    const disabled = !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);
    return html`
      <div class="exclusion-lists">
        <div class="exclusion-lists--header">
          <gz-button
            class="list-button"
            color="primary"
            type="quiet"
            buttonFontSize="small"
            buttonHeight="small"
            @click="${this.createNewList}"
            .disabled="${disabled}"
            >${htmlDecode(this.newListString)}</gz-button
          >
        </div>

        <div class="exclusion-lists--content">
          <gz-list-item @click="${this.showAllExclusions}" .active="${!this.activeList}">
            ${htmlDecode(this.allExclusionsString)}
          </gz-list-item>

          <div class="exclusion-list">
            <gz-title fontSize="11" fontweight="600" class="exclusion-list--title">
              ${htmlDecode(this.userExclusionListsTitle.toUpperCase())}
            </gz-title>

            ${this.renderLists(this.userExclusionLists)}
          </div>

          <div class="exclusion-list">
            <gz-title fontSize="11" fontweight="600" class="exclusion-list--title">
              ${htmlDecode(this.companyExclusionListsTitle.toUpperCase())}
            </gz-title>

            ${this.renderLists(this.companyExclusionLists)}
          </div>
        </div>
      </div>
    `;
  }

  private renderLists(lists: ExclusionListItem[]): TemplateResult | undefined {
    if (!lists.length) {
      return;
    }
    return html`${lists.map(item => this.renderListItem(item))}`;
  }

  private renderListItem(item: ExclusionListItem) {
    item = desanitizeObject(item);
    const isActive = this.activeList && this.activeList.value === item.value;

    return html`<gz-list-item .active="${isActive}" data-list="${JSON.stringify(item)}" @click="${this.showList}">
      ${item.label}
    </gz-list-item>`;
  }

  public setActiveListItem(listItem: ExclusionListItem | null) {
    this.activeList = listItem;
  }

  private createNewList() {
    this.dispatchEvent(
      new CustomEvent(GlobalExclusionsEvents.CREATE_NEW_EXCLUSION_LIST, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  private showList(event) {
    const value = event.target.getAttribute('data-list');
    const list = value ? JSON.parse(value) : undefined;

    this.dispatchEvent(
      new CustomEvent(GlobalExclusionsEvents.SHOW_EXCLUSION_LIST, {
        detail: {
          list: list,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private showAllExclusions(event) {
    this.setActiveListItem(null);
    this.dispatchEvent(
      new CustomEvent(GlobalExclusionsEvents.SHOW_ALL_EXCLUSION_LIST, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }
}
